import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Laptop, Plus, Trash2, Webhook } from "lucide-react";
import type { ApiKeyScope, WebhookEvent } from "@hub/shared";
import { Button, Checkbox, Field, Input, Pill, Spinner, formatDate, relativeTime, toast } from "@/ui";
import { api, ApiError, isUnavailableRoute } from "@/api/client";
import { useSession } from "@/app/session";
import { ConfirmModal } from "@/features/contact/ConfirmModal";
import s from "../shared.module.css";

const SCOPES: { id: ApiKeyScope; label: string }[] = [
  { id: "read:people", label: "Read people" },
  { id: "read:needs", label: "Read needs and offers" },
  { id: "read:matches", label: "Read matches" },
  { id: "write:notes", label: "Write notes" },
];
const EVENTS: { id: WebhookEvent; label: string }[] = [
  { id: "contact.created", label: "Person created" },
  { id: "note.created", label: "Note created" },
  { id: "match.waiting", label: "Introduction waiting on you" },
  { id: "match.accepted", label: "Introduction accepted" },
  { id: "connection.accepted", label: "Connection accepted" },
];

function Unavailable({ what }: { what: string }) {
  return <div className="u-small u-muted" data-testid="route-unavailable">Not available on this server. {what} needs a newer Hub server.</div>;
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

export function SessionsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["account", "sessions"], queryFn: () => api.account.sessions(), retry: false });
  const revoke = useMutation({
    mutationFn: (id: string) => api.account.revokeSession(id),
    onSuccess: () => { toast("Signed out of that device", "success"); qc.invalidateQueries({ queryKey: ["account", "sessions"] }); },
    onError: (e) => toast(errorText(e, "Could not sign out that device"), "danger"),
  });
  return (
    <section className={s.section} style={{ marginTop: 20 }} data-testid="sessions-section">
      <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 4 }}><Laptop size={14} /> Devices and sessions</div>
      <div className="u-small u-muted" style={{ marginBottom: 8 }}>Everywhere this account is signed in. Signing a device out takes effect on its next request.</div>
      {q.isLoading && <Spinner />}
      {q.isError && (isUnavailableRoute(q.error) ? <Unavailable what="The session list" /> : <div className="u-small" style={{ color: "var(--danger)" }}>{errorText(q.error, "Could not load sessions")}</div>)}
      {(q.data?.sessions ?? []).map((sess) => (
        <div key={sess.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
          <div className="u-grow" style={{ minWidth: 0 }}>
            <div className="u-row" style={{ gap: 6 }}>
              <span style={{ fontWeight: 500 }}>{sess.deviceName ?? "Unnamed device"}</span>
              <Pill>{sess.platform}</Pill>
              {sess.current && <Pill tone="success">This device</Pill>}
            </div>
            <div className="u-small u-muted">Signed in {formatDate(sess.created)}{sess.lastSeenAt ? `, last seen ${relativeTime(sess.lastSeenAt)}` : ""}</div>
          </div>
          <Button size="sm" variant={sess.current ? "ghost" : "outline"} loading={revoke.isPending && revoke.variables === sess.id} onClick={() => revoke.mutate(sess.id)}>
            {sess.current ? "Sign out here" : "Sign out of this device"}
          </Button>
        </div>
      ))}
      {q.isSuccess && q.data.sessions.length === 0 && <div className="u-small u-muted">No other sessions.</div>}
    </section>
  );
}

export function ApiKeysSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["account", "apiKeys"], queryFn: () => api.account.apiKeys(), retry: false });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Set<ApiKeyScope>>(new Set(["read:people"]));
  const [secret, setSecret] = useState<{ name: string; secret: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api.account.createApiKey({ name: name.trim(), scopes: Array.from(scopes) }),
    onSuccess: (res) => { setSecret({ name: res.key.name, secret: res.secret }); setCreating(false); setName(""); qc.invalidateQueries({ queryKey: ["account", "apiKeys"] }); },
    onError: (e) => toast(errorText(e, "Could not create the key"), "danger"),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.account.revokeApiKey(id),
    onSuccess: () => { toast("Key revoked", "success"); qc.invalidateQueries({ queryKey: ["account", "apiKeys"] }); },
    onError: (e) => toast(errorText(e, "Could not revoke the key"), "danger"),
  });
  const unavailable = q.isError && isUnavailableRoute(q.error);
  return (
    <section className={s.section} style={{ marginTop: 20 }} data-testid="api-keys-section">
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><KeyRound size={14} /> API keys</span>
        {!unavailable && <Button size="sm" icon={<Plus size={13} />} onClick={() => setCreating((c) => !c)}>New key</Button>}
      </div>
      <div className="u-small u-muted" style={{ marginBottom: 8 }}>Read your synced people, needs and matches from scripts and other tools. Keys carry only the scopes you pick.</div>
      {q.isLoading && <Spinner />}
      {q.isError && (unavailable ? <Unavailable what="API keys" /> : <div className="u-small" style={{ color: "var(--danger)" }}>{errorText(q.error, "Could not load keys")}</div>)}
      {secret && (
        <div style={{ padding: 10, border: "1px solid var(--success)", background: "var(--success-soft)", borderRadius: 8, marginBottom: 10 }} data-testid="api-key-secret">
          <div style={{ fontWeight: 600 }}>Copy the key for {secret.name} now</div>
          <div className="u-small u-muted">It is shown once. Hub stores only a hash.</div>
          <div className="u-row" style={{ marginTop: 6 }}>
            <code className="u-mono u-small" style={{ wordBreak: "break-all", flex: 1 }}>{secret.secret}</code>
            <Button size="sm" icon={<Copy size={12} />} onClick={() => navigator.clipboard?.writeText(secret.secret).then(() => toast("Copied")).catch(() => toast("Select and copy the key by hand"))}>Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setSecret(null)}>Done</Button>
          </div>
        </div>
      )}
      {creating && (
        <div className="u-col" style={{ gap: 8, padding: 10, border: "1px solid var(--border)", borderRadius: 8, marginBottom: 10 }}>
          <Field label="Name" hint="Where this key is used, for example a reporting script."><Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Reporting script" /></Field>
          <div className="u-row" style={{ gap: 12, flexWrap: "wrap" }}>
            {SCOPES.map((sc) => (
              <label key={sc.id} className="u-row u-small" style={{ gap: 4 }}>
                <Checkbox checked={scopes.has(sc.id)} onChange={(e) => setScopes((prev) => { const n = new Set(prev); if (e.target.checked) n.add(sc.id); else n.delete(sc.id); return n; })} /> {sc.label}
              </label>
            ))}
          </div>
          <div className="u-row" style={{ justifyContent: "flex-end" }}>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={!name.trim() || scopes.size === 0} loading={create.isPending} onClick={() => create.mutate()}>Create key</Button>
          </div>
        </div>
      )}
      {(q.data?.keys ?? []).map((k) => (
        <div key={k.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
          <div className="u-grow" style={{ minWidth: 0 }}>
            <div className="u-row" style={{ gap: 6 }}><span style={{ fontWeight: 500 }}>{k.name}</span><code className="u-mono u-small u-muted">{k.prefix}...</code></div>
            <div className="u-small u-muted">{k.scopes.join(", ")}. Created {formatDate(k.created)}{k.lastUsedAt ? `, last used ${relativeTime(k.lastUsedAt)}` : ", never used"}</div>
          </div>
          <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} loading={revoke.isPending && revoke.variables === k.id} onClick={() => { if (window.confirm(`Revoke the key ${k.name}? Anything using it stops working.`)) revoke.mutate(k.id); }}>Revoke</Button>
        </div>
      ))}
      {q.isSuccess && q.data.keys.length === 0 && <div className="u-small u-muted">No keys yet.</div>}
    </section>
  );
}

export function WebhooksSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["account", "webhooks"], queryFn: () => api.account.webhooks(), retry: false });
  const [creating, setCreating] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<Set<WebhookEvent>>(new Set(["match.waiting"]));
  const [signingSecret, setSigningSecret] = useState<{ url: string; secret: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api.account.createWebhook({ url: url.trim(), events: Array.from(events) }),
    onSuccess: (res) => { setSigningSecret({ url: res.webhook.url, secret: res.secret }); toast("Webhook added", "success"); setCreating(false); setUrl(""); qc.invalidateQueries({ queryKey: ["account", "webhooks"] }); },
    onError: (e) => toast(errorText(e, "Could not add the webhook"), "danger"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.account.deleteWebhook(id),
    onSuccess: () => { toast("Webhook removed", "success"); qc.invalidateQueries({ queryKey: ["account", "webhooks"] }); },
    onError: (e) => toast(errorText(e, "Could not remove the webhook"), "danger"),
  });
  const unavailable = q.isError && isUnavailableRoute(q.error);
  const validUrl = /^https:\/\/.+/i.test(url.trim()) || /^http:\/\/(localhost|127\.0\.0\.1)/i.test(url.trim());
  return (
    <section className={s.section} style={{ marginTop: 20 }} data-testid="webhooks-section">
      <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
        <span className="u-row" style={{ gap: 6, fontWeight: 600 }}><Webhook size={14} /> Webhooks</span>
        {!unavailable && <Button size="sm" icon={<Plus size={13} />} onClick={() => setCreating((c) => !c)}>Add webhook</Button>}
      </div>
      <div className="u-small u-muted" style={{ marginBottom: 8 }}>Hub posts a JSON event to your URL when something happens. Payloads carry ids and titles, never message bodies.</div>
      {signingSecret && (
        <div style={{ padding: 10, border: "1px solid var(--success)", background: "var(--success-soft)", borderRadius: 8, marginBottom: 10 }} data-testid="webhook-secret">
          <div style={{ fontWeight: 600 }}>Copy the signing secret for {signingSecret.url} now</div>
          <div className="u-small u-muted">Hub signs every delivery with it (X-Hub-Signature). It is not shown again.</div>
          <div className="u-row" style={{ marginTop: 6 }}>
            <code className="u-mono u-small" style={{ wordBreak: "break-all", flex: 1 }}>{signingSecret.secret}</code>
            <Button size="sm" icon={<Copy size={12} />} onClick={() => navigator.clipboard?.writeText(signingSecret.secret).then(() => toast("Copied")).catch(() => toast("Select and copy the secret by hand"))}>Copy</Button>
            <Button size="sm" variant="ghost" onClick={() => setSigningSecret(null)}>Done</Button>
          </div>
        </div>
      )}
      {q.isLoading && <Spinner />}
      {q.isError && (unavailable ? <Unavailable what="Webhooks" /> : <div className="u-small" style={{ color: "var(--danger)" }}>{errorText(q.error, "Could not load webhooks")}</div>)}
      {creating && (
        <div className="u-col" style={{ gap: 8, padding: 10, border: "1px solid var(--border)", borderRadius: 8, marginBottom: 10 }}>
          <Field label="URL" hint="https only (http is accepted for localhost)."><Input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hub-events" className="u-mono" /></Field>
          <div className="u-row" style={{ gap: 12, flexWrap: "wrap" }}>
            {EVENTS.map((ev) => (
              <label key={ev.id} className="u-row u-small" style={{ gap: 4 }}>
                <Checkbox checked={events.has(ev.id)} onChange={(e) => setEvents((prev) => { const n = new Set(prev); if (e.target.checked) n.add(ev.id); else n.delete(ev.id); return n; })} /> {ev.label}
              </label>
            ))}
          </div>
          <div className="u-row" style={{ justifyContent: "flex-end" }}>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={!validUrl || events.size === 0} loading={create.isPending} onClick={() => create.mutate()}>Add</Button>
          </div>
        </div>
      )}
      {(q.data?.webhooks ?? []).map((w) => (
        <div key={w.id} className="u-row" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
          <div className="u-grow" style={{ minWidth: 0 }}>
            <div className="u-mono u-small u-truncate" title={w.url}>{w.url}</div>
            <div className="u-small u-muted">{w.events.join(", ")}. {w.lastDeliveryAt ? `Last delivery ${relativeTime(w.lastDeliveryAt)}${w.lastStatus ? ` (HTTP ${w.lastStatus})` : ""}` : "No deliveries yet"}</div>
          </div>
          {w.lastStatus !== null && w.lastStatus >= 400 && <Pill tone="danger">Failing</Pill>}
          <Button size="sm" variant="ghost" icon={<Trash2 size={12} />} loading={remove.isPending && remove.variables === w.id} onClick={() => { if (window.confirm("Remove this webhook?")) remove.mutate(w.id); }}>Remove</Button>
        </div>
      ))}
      {q.isSuccess && q.data.webhooks.length === 0 && <div className="u-small u-muted">No webhooks yet.</div>}
    </section>
  );
}

export function DangerZone({ onDeleted }: { onDeleted: () => void }) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const del = useMutation({
    mutationFn: () => api.account.deleteAccount({ confirmEmail: confirmEmail.trim().toLowerCase() }),
    onSuccess: () => { toast("Account deleted"); onDeleted(); },
    onError: (e) => { if (isUnavailableRoute(e)) { setUnavailable(true); setOpen(false); } else toast(errorText(e, "Could not delete the account"), "danger"); },
  });
  const email = session.user?.email ?? "";
  return (
    <section className={s.section} style={{ marginTop: 20, padding: 12, border: "1px solid var(--danger)", borderRadius: 8 }} data-testid="danger-zone">
      <div style={{ fontWeight: 600, color: "var(--danger)" }}>Danger zone</div>
      <div className="u-row" style={{ justifyContent: "space-between", marginTop: 6, gap: 12 }}>
        <div>
          <div style={{ fontWeight: 500 }}>Delete account</div>
          <div className="u-small u-muted">Removes your account, every synced workspace you own, your needs, offers, introductions and messages from the server. Data on this device stays until you reset it.</div>
          {unavailable && <Unavailable what="Account deletion" />}
        </div>
        <Button size="sm" variant="danger" onClick={() => setOpen(true)} disabled={unavailable}>Delete account</Button>
      </div>
      <ConfirmModal
        open={open}
        title="Delete your account?"
        body={
          <div className="u-col" style={{ gap: 8 }}>
            <div>This cannot be undone. Type your email address to confirm.</div>
            <Input autoFocus value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={email} aria-label="Confirm email" />
          </div>
        }
        confirmLabel="Delete my account"
        danger
        disabled={confirmEmail.trim().toLowerCase() !== email.toLowerCase() || !email}
        loading={del.isPending}
        onClose={() => setOpen(false)}
        onConfirm={() => del.mutate()}
      />
    </section>
  );
}
